import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import {
  ORBI_HOW_IT_WORKS_LABEL,
  ORBI_SUGGESTED_PROMPTS,
  ORBI_SUGGESTED_PROMPT_COUNT,
  OrbiSuggestedPrompt,
} from '@patient-ui/shared/constants';

/**
 * Suggested prompts (PCSA-13159). Shown WHENEVER the input is empty — not only
 * on an empty thread: the AC asks for them "at any point during a conversation
 * when the input field is empty" (AC 1.2), on a full clear of the field (AC 1.3)
 * and again "when the agent response is returned" (AC 1.4), which is also how the
 * Figma canvas draws them — a row of chips under the latest response, above the
 * disclaimer.
 *
 * `ORBI_SUGGESTED_PROMPT_COUNT` prompts — three, per design review 08/21 — are
 * drawn at random from the approved list in the story's Additional Information:
 * the same thirteen mobile carries, shuffled and taken N at a time exactly as
 * mobile does, N being the one thing web sets differently (see the constant). The
 * set ROTATES each time `conversationKey` changes, which the host ties to an
 * answer landing (design review 08/28), and a rotation always changes the row —
 * see `pickRandomPrompts`.
 * Selecting one PRE-FILLS the input (no auto-send — the host wires that). A
 * distinct "How does this work?" chip (net-new for web, visually differentiated
 * by the resting gradient) instead asks for the explainer via the `howItWorks`
 * output, which the host answers as a conversation turn. That one chip is offered
 * on a NEW conversation only (`showHowItWorks`) — the suggested prompts are not.
 *
 * Presentational: emits `promptSelected(fullPrompt)` / `howItWorks`; the parent
 * (thread) owns showing/hiding this based on the input's empty/loading/locked
 * state, per the AC. How they are LAID OUT (one vertical stack at every width,
 * riding the thread feed's own scroll — QA 08/21 replaced the mobile/tablet
 * horizontally scrolling row of AC 4.1 with the desktop stack) is handled purely
 * in the SCSS.
 */
@Component({
  selector: 'patient-ui-orbi-suggested-prompts',
  templateUrl: './orbi-suggested-prompts.component.html',
  styleUrls: ['./orbi-suggested-prompts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiSuggestedPromptsComponent implements OnChanges {
  /** Emits the fully formed question to pre-fill the input (no auto-send). */
  @Output() promptSelected = new EventEmitter<string>();
  /** Emits when the distinct "How does this work?" chip is chosen. */
  @Output() howItWorks = new EventEmitter<void>();

  /**
   * How the chips sit in their column. The Figma draws BOTH: centered under the
   * centered greeting while the conversation is empty, and left-aligned once the
   * chips follow a response (they line up with the agent bubble above them). One
   * instance serves both states — see the host's `align` binding.
   */
  @Input() align: 'center' | 'start' = 'center';

  /**
   * What the current subset is keyed to. Any change re-picks it (see
   * `ngOnChanges`) — so the host decides HOW OFTEN the chips rotate by choosing
   * what it puts in here, and this component only owns the drawing.
   *
   * The key is deliberately NOT the conversation alone any more (design review
   * 08/28: "for each AI response rotate the suggested prompts — it's not getting
   * rotated after a response arrives, it gets rotated when I close and reopen the
   * conversation"). The host now folds the answer count into it, so a landed
   * response rotates the set; see the thread's `promptsKey$`.
   *
   * What it must never be keyed to is anything that changes WHILE the chips are on
   * screen and reachable: re-rolling the subset every time the user typed a
   * character and deleted it churned the list under them, and a chip they were
   * about to press could vanish (AC 1.2/1.3). An answer landing is safe in a way
   * a keystroke is not — the chips are hidden for the whole send (`showPrompts$`
   * drops them while `sendPending`), so the swap happens off screen and the row
   * the user gets back is simply a new one.
   */
  @Input() conversationKey: string | null = null;

  /**
   * Whether the distinct "How does this work?" chip is offered (design review
   * 08/19). It belongs to a NEW conversation only: it explains the feature to a
   * patient who has not used it yet, so once turns exist the answer to "how does
   * this work" is on screen above the chips and the pill is just clutter competing
   * with the real questions — and it wears the resting gradient, so it drew the
   * eye hardest of the set.
   *
   * An input rather than something derived here: this component knows nothing about
   * the conversation, and the host already holds that state (`threadEmpty$`).
   * Defaults to shown so a host that offers no thread context still gets the
   * explainer.
   */
  @Input() showHowItWorks = true;

  readonly howItWorksLabel = ORBI_HOW_IT_WORKS_LABEL;

  /**
   * The chips on screen: `ORBI_SUGGESTED_PROMPT_COUNT` drawn at random from the
   * approved list (mobile's method — shuffle, then take N). Re-picked whenever
   * `conversationKey` changes; the initializer covers a host that binds no key
   * at all.
   */
  prompts: OrbiSuggestedPrompt[] = this.pickRandomPrompts();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['conversationKey']) {
      // Exclude what is being retired, so the row the user gets back is visibly a
      // DIFFERENT row. A plain redraw is what "it's not getting rotated" was
      // reporting: three of thirteen redrawn independently repeat at least one
      // chip about half the time and can land on the same three outright, so a
      // rotation that did fire could still look like nothing had happened.
      this.prompts = this.pickRandomPrompts(this.prompts);
    }
  }

  onSelect(prompt: OrbiSuggestedPrompt): void {
    this.promptSelected.emit(prompt.fullPrompt);
  }

  onHowItWorks(): void {
    this.howItWorks.emit();
  }

  trackByLabel(_index: number, prompt: OrbiSuggestedPrompt): string {
    return prompt.shortPrompt;
  }

  /**
   * `ORBI_SUGGESTED_PROMPT_COUNT` prompts at random, preferring ones NOT in
   * `retiring` — mobile's shuffle-and-take, with the chips currently on screen
   * pushed to the back of the queue so consecutive draws differ.
   *
   * The exclusion is a preference, not a filter: the retired chips stay available
   * behind the fresh ones, so the list can never come back short. With thirteen
   * approved prompts and three shown that fallback is unreachable today (ten are
   * always eligible) — it is what keeps the row full if the approved list is ever
   * trimmed or the count raised.
   */
  private pickRandomPrompts(
    retiring: readonly OrbiSuggestedPrompt[] = []
  ): OrbiSuggestedPrompt[] {
    const onScreen = new Set(retiring.map((prompt) => prompt.shortPrompt));
    const eligible = (wanted: boolean) =>
      this.shuffle(
        ORBI_SUGGESTED_PROMPTS.filter(
          (prompt) => onScreen.has(prompt.shortPrompt) === wanted
        )
      );
    return [...eligible(false), ...eligible(true)].slice(
      0,
      ORBI_SUGGESTED_PROMPT_COUNT
    );
  }

  /**
   * Fisher–Yates over a copy — mirrors mobile's
   * `List.from(allPrompts)..shuffle()`. Copies first: `ORBI_SUGGESTED_PROMPTS` is
   * the shared approved list.
   */
  private shuffle(prompts: readonly OrbiSuggestedPrompt[]) {
    const pool = [...prompts];
    for (let i = pool.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pool[i], pool[j]] = [pool[j], pool[i]];
    }
    return pool;
  }
}
